import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { organizationsAPI } from '../services/organizationsAPI'
import { usersAPI } from '../services/usersAPI'
import api from '../services/api'
import Card, { CardHeader, CardTitle, CardContent } from '../components/UI/Card'
import LoadingSpinner from '../components/UI/LoadingSpinner'
import Button from '../components/UI/Button'
import ConfirmationModal from '../components/Modals/ConfirmationModal'
import {
  Building2,
  Users,
  Globe,
  MapPin,
  Calendar,
  Settings,
  Palette,
  Shield,
  CreditCard,
  ArrowLeft,
  Edit,
  Eye,
  Trash2,
  FileText
} from 'lucide-react'
import SSOConfigurationTab from '../components/OrganizationTabs/SSOConfigurationTab'
import SubscriptionsTab from '../components/OrganizationTabs/SubscriptionsTab'
import SASUsersTab from '../components/OrganizationTabs/SASUsersTab'
import UsersTab from '../components/OrganizationTabs/UsersTab'
import InvoiceTab from '../components/OrganizationTabs/InvoiceTab'
import OrganizationBrandingTab from '../components/OrganizationTabs/OrganizationBrandingTab'
 
export default function OrganizationDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasPermission } = useAuth()
  const [activeTab, setActiveTab] = useState('overview')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorDetails, setErrorDetails] = useState(null)
 
  const { data, isLoading, refetch } = useQuery(
    ['organization', id],
    () => organizationsAPI.getById(id),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 15 * 60 * 1000, // 15 minutes
      refetchOnMount: false,
    }
  )
  const organization = data?.data?.data?.organization || null
 
  // Fetch active users count - optimized caching
  const { data: activeUsersData } = useQuery(
    ['active-users', id],
    () => usersAPI.getByOrganization(id, { status: 'active' }),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
    }
  )
  const activeUsers = activeUsersData?.data?.data?.users || []
  const activeUserCount = activeUsers.filter(user => user.status === 'active').length
 
  // Fetch subscriptions for active subscription count
  const { data: subscriptionsData } = useQuery(
    ['subscriptions', id],
    () => api.get(`/subscriptions/organization/${id}`).then(r => r.data),
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
      cacheTime: 15 * 60 * 1000,
      refetchOnMount: false,
    }
  )
  const subscriptions = subscriptionsData?.data?.subscriptions || []
  const activeSubscriptionCount = subscriptions.filter(sub => sub.status === 'active').length
 
  // Debug logging
  console.log('OrganizationDetail - API Response:', data)
  console.log('OrganizationDetail - Organization Data:', organization)
 
  // Delete organization mutation (soft delete)
  const deleteOrganizationMutation = useMutation(
    () => organizationsAPI.delete(id),
    {
      onSuccess: () => {
        // Invalidate and refetch organization queries
        queryClient.invalidateQueries(['organizations'])
        queryClient.invalidateQueries(['organization', id])
        // Invalidate user queries for this organization so inactive users appear in archive tab
        queryClient.invalidateQueries(['users', id])
        queryClient.invalidateQueries(['archived-users', id])
        // Invalidate archive users query so newly archived users appear immediately
        queryClient.invalidateQueries(['archived-organization-users'])
        // Navigate to organizations list with archive tab active
        navigate('/organizations?tab=archive')
      },
      onError: (error) => {
        console.error('Delete organization error:', error)
        // Show detailed error message in modal
        const errorData = error.response?.data
        setErrorDetails(errorData)
        setShowErrorModal(true)
      }
    }
  )
 
  const handleDeleteClick = () => {
    setShowDeleteModal(true)
  }
 
  const handleDeleteConfirm = () => {
    deleteOrganizationMutation.mutate()
    setShowDeleteModal(false)
  }
 
  const handleDeleteCancel = () => {
    setShowDeleteModal(false)
  }
 
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }
 
  if (!organization) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-secondary-900 mb-2">Organization not found</h2>
        <p className="text-secondary-600 mb-4">The organization you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/organizations')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Organizations
        </Button>
      </div>
    )
  }
 
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { color: 'bg-green-100 text-green-800', label: 'Active' },
      inactive: { color: 'bg-gray-100 text-gray-800', label: 'Inactive' },
      suspended: { color: 'bg-red-100 text-red-800', label: 'Suspended' }
    }
   
    const config = statusConfig[status] || statusConfig.inactive
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    )
  }
 
  const tabs = [
    { id: 'overview', label: 'Overview', icon: Building2 },
    { id: 'invoice', label: 'Invoice', icon: FileText },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard },
    { id: 'branding', label: 'Branding', icon: Palette },
    { id: 'sso', label: 'SSO Config', icon: Shield },
    { id: 'settings', label: 'Settings', icon: Settings }
  ]
 
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Building2 className="h-5 w-5 mr-2" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Organization Name</p>
                  <p className="font-medium text-lg">{organization.name}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Email</p>
                  <p className="font-medium">{organization.email}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Website</p>
                  <p className="font-medium">
                    {organization.website ? (
                      <a href={organization.website} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">
                        {organization.website}
                      </a>
                    ) : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Industry</p>
                  <p className="font-medium">{organization.industry || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Employee Count</p>
                  <p className="font-medium">{organization.employee_count || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Projects</p>
                  <p className="font-medium">{organization.projects || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Units</p>
                  <p className="font-medium">{organization.units || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Location</p>
                  <p className="font-medium">{organization.location || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Country</p>
                  <p className="font-medium">{organization.country || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Status</p>
                  <div className="mt-1">{getStatusBadge(organization.status)}</div>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Created</p>
                  <p className="font-medium">{new Date(organization.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Last Updated</p>
                  <p className="font-medium">{new Date(organization.updated_at).toLocaleDateString()}</p>
                </div>
              </CardContent>
            </Card>
 
            {/* Statistics */}
            <Card>
              <CardHeader>
                <CardTitle>Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary-600">{activeUserCount}</div>
                    <div className="text-sm text-secondary-600">Total Users</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{activeSubscriptionCount}</div>
                    <div className="text-sm text-secondary-600">Total Subscriptions</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )
 
      case 'invoice':
        return (
          <InvoiceTab organizationId={id} />
        )
 
      case 'users':
        return (
          <UsersTab organizationId={id} />
        )
 
      case 'subscriptions':
        return (
          <SubscriptionsTab organizationId={id} />
        )
 
      case 'branding':
        return (
          <OrganizationBrandingTab
            organization={organization}
            canEdit={hasPermission('organizations.update')}
          />
        )
 
      case 'sso':
        return (
          <SSOConfigurationTab organizationId={id} />
        )
 
      case 'settings':
        return (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Settings className="h-5 w-5 mr-2" />
                Organization Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-secondary-600 mb-2">Organization ID</p>
                  <p className="font-mono text-sm bg-secondary-100 px-2 py-1 rounded">{organization.id}</p>
                </div>
                <div>
                  <p className="text-sm text-secondary-600 mb-2">Status</p>
                  <div className="mt-1">{getStatusBadge(organization.status)}</div>
                </div>
              </div>
             
              <div className="pt-4 border-t border-secondary-200 space-y-4">
                <div className="flex space-x-3">
                  {hasPermission('organizations.update') && (
                    <Button onClick={() => navigate(`/organizations/${id}/edit`)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit Organization
                    </Button>
                  )}
                  <Button variant="outline" onClick={() => setActiveTab('users')}>
                    <Users className="h-4 w-4 mr-2" />
                    Manage Users
                  </Button>
                </div>
                <div className="flex space-x-3">
                  <Button variant="outline" onClick={() => setActiveTab('subscriptions')}>
                    <CreditCard className="h-4 w-4 mr-2" />
                    View Subscriptions
                  </Button>
                  <Button variant="outline" onClick={() => setActiveTab('overview')}>
                    <Eye className="h-4 w-4 mr-2" />
                    View Analytics
                  </Button>
                </div>
                {hasPermission('organizations.delete') && (
                  <div className="pt-4 border-t border-secondary-200">
                    <Button
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={handleDeleteClick}
                      disabled={deleteOrganizationMutation.isLoading}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {deleteOrganizationMutation.isLoading ? 'Deleting...' : 'Delete Organization'}
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )
 
      default:
        return null
    }
  }
 
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="outline" onClick={() => navigate('/organizations')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-900">{organization.name}</h1>
            <p className="text-secondary-600">{organization.email}</p>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-2">
          {hasPermission('organizations.update') && (
            <Button onClick={() => navigate(`/organizations/${id}/edit`)}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
        </div>
      </div>
 
      {/* Tabs */}
      <div className="border-b border-secondary-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-secondary-500 hover:text-secondary-700 hover:border-secondary-300'
                }`}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>
 
      {/* Tab Content */}
      {renderTabContent()}
 
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Organization"
        message={`Are you sure you want to delete "${organization?.name}"? This will move the organization to the archive. The organization must have no active subscriptions to be deleted.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="warning"
      />
 
      {/* Error Modal for Subscription Errors */}
      {showErrorModal && errorDetails && (
        <ConfirmationModal
          isOpen={showErrorModal}
          onClose={() => {
            setShowErrorModal(false)
            setErrorDetails(null)
          }}
          onConfirm={() => {
            setShowErrorModal(false)
            setErrorDetails(null)
          }}
          title="Cannot Delete Organization"
          message={(() => {
            let message = errorDetails.error || errorDetails.message || 'Failed to delete organization. Please try again.'
           
            if (errorDetails.details?.subscriptions && errorDetails.details.subscriptions.length > 0) {
              message += '\n\nSubscriptions:'
              errorDetails.details.subscriptions.forEach((sub, idx) => {
                message += `\n${idx + 1}. ${sub.product_name || 'Unknown Product'} (${sub.status})`
              })
            }
           
            return message
          })()}
          confirmText="OK"
          cancelText={null}
          type="warning"
        />
      )}
    </div>
  )
}
 
 
 
 
 
 
 